import React from "react";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import StarIcon from "@mui/icons-material/Star";

const userTestimonials = [
  {
    avatar: (
      <Avatar
        alt="Ankit Gupta"
        sx={{ bgcolor: "#000000", color: "#FFFFFF", width: 48, height: 48, fontSize: 20, fontWeight: "bold" }}
      >
        A
      </Avatar>
    ),
    name: "Ankit Gupta",
    occupation: "Mumbai, India",
    testimonial:
      "I love this website. I just got paid 20rm and it was all so quick, just has to install an App",
  },
  {
    avatar: (
      <Avatar
        alt="Neeraj Khera"
        sx={{ bgcolor: "#000000", color: "#FFFFFF", width: 48, height: 48, fontSize: 20, fontWeight: "bold" }}
      >
        N
      </Avatar>
    ),
    name: "Neeraj Khera",
    occupation: "Noida, India",
    testimonial: "Bahut hi sahi hai bhai, roj paisa milta hai paytm par",
  },
  {
    avatar: (
      <Avatar
        alt="Jeet Mody"
        sx={{ bgcolor: "#000000", color: "#FFFFFF", width: 48, height: 48, fontSize: 20, fontWeight: "bold" }}
      >
        J
      </Avatar>
    ),
    name: "Jeet Mody",
    occupation: "Bangalore, India",
    testimonial:
      "Best app out there if you are a student, daily new tasks to install different Apps and easy money",
  },
];

const MobileTestimonial: React.FC<{
  testimonial: (typeof userTestimonials)[0];
  isActive: boolean;
}> = ({ testimonial, isActive }) => (
  <div
    style={{
      display: isActive ? "block" : "none",
      transition: "opacity 0.5s ease-in-out",
      width: "100%",
    }}
  >
    <Box
      id="testimonials"
      data-track="testimonial"
      data-name={`testimonial_mobile_${testimonial.name.replace(/\s+/g, '_').toLowerCase()}`}
      sx={{
        background: "linear-gradient(180deg, #FFFDE7 0%, #FFC107 100%)",
        borderRadius: "20px",
        p: 4,
        mx: 2,
        boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
        color: "#000", // Black text
        minHeight: "350px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        textAlign: "left",
      }}
    >
      <Box>
        <Box display="flex" gap={0.5} mb={3}>
          {[...Array(5)].map((_, i) => (
            <StarIcon key={i} sx={{ color: "#F57F17", fontSize: 28 }} />
          ))}
        </Box>
        <Typography
          variant="body1"
          sx={{
            color: "#000",
            fontSize: "18px",
            lineHeight: 1.5,
            fontWeight: 500,
            mb: 4,
            fontFamily: "General-Sans-bold, sans-serif"
          }}
        >
          {testimonial.testimonial}
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" gap={2}>
        {testimonial.avatar}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, fontSize: "16px", color: "#000" }}
          >
            {testimonial.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: "14px", color: "rgba(0,0,0,0.7)" }}
          >
            {testimonial.occupation}
          </Typography>
        </Box>
      </Box>
    </Box>
  </div>
);

export default function Testimonials() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTestimonial, setActiveTestimonial] = React.useState(0);

  // Sliding feature for mobile devices
  React.useEffect(() => {
    if (isMobile) {
      const interval = setInterval(() => {
        setActiveTestimonial((prev) => (prev + 1) % userTestimonials.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isMobile]);

  // Swipe Handler Logic
  const [touchStart, setTouchStart] = React.useState<number | null>(null);
  const [touchEnd, setTouchEnd] = React.useState<number | null>(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      setActiveTestimonial((prev) => (prev + 1) % userTestimonials.length);
    }
    if (isRightSwipe) {
      setActiveTestimonial((prev) => (prev - 1 + userTestimonials.length) % userTestimonials.length);
    }
  };

  if (isMobile) {
    // Mobile-friendly design
    return (
      <Container
        id="testimonials"
        sx={{
          textAlign: "center",
          pt: 1,
          pb: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Box mb={2}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: "12px",
              letterSpacing: 1.5,
              color: "#FB8C00", // Orange color for Title
              mb: 1,
            }}
          >
            TESTIMONIAL
          </Typography>
          <Typography
            variant="h4"
            component="div"
            sx={{
              fontWeight: 600,
              fontSize: "32px",
              color: "#1A237E", // Dark Blue for Heading
              lineHeight: 1.2,
              mb: 2,
              fontFamily: "General-Sans-bold, sans-serif"
            }}
          >
            Read All About Our Users Experiences
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#455A64",
              maxWidth: "100%",
              fontWeight: 400,
              fontSize: "15px",
              lineHeight: 1.6,
              px: 2,
            }}
          >
            Discover how we helped them earn extra money by installing Apps and
            completing tasks. Our simple and secure website is trusted by
            thousands of users across the country.
          </Typography>
        </Box>

        {/* Swipeable Container */}
        <Box
          sx={{ position: "relative", width: "100%", mb: 2 }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {userTestimonials.map((testimonial, index) => (
            <MobileTestimonial
              key={index}
              testimonial={testimonial}
              isActive={activeTestimonial === index}
            />
          ))}
        </Box>

        <Box display="flex" justifyContent="center" gap={1} mb={2}>
          {userTestimonials.map((_, index) => (
            <Box
              key={index}
              onClick={() => setActiveTestimonial(index)}
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor:
                  activeTestimonial === index ? "#FFC107" : "#E0E0E0",
                cursor: "pointer",
                transition: "background-color 0.3s",
              }}
            ></Box>
          ))}
        </Box>

        {/* Partners Section - Keeping as is */}
        {/* <Box sx={{ mt: 2, textAlign: "center", width: "100%", overflow: "hidden" }}>
           <Typography ... >RECOGNIZED AS SAFE TO USE BY:</Typography>
           ...
        </Box> */}
      </Container>
    );
  }

  // Desktop Fallback
  return (
    <Container
      id="testimonials"
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: { xs: 3, sm: 6 },
      }}
    >
      {/* ... simplified desktop view ... */}
      <Typography component="h2" variant="h4" color="text.primary">
        Testimonials
      </Typography>
      <Grid container spacing={2}>
        {userTestimonials.map((testimonial, index) => (
          <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: "flex" }}>
            <Card
              sx={{ p: 2, backgroundColor: "#FFF8E1" }}
              data-track="testimonial"
              data-name={`testimonial_desktop_${testimonial.name.replace(/\s+/g, '_').toLowerCase()}`}
            >
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {testimonial.testimonial}
                </Typography>
              </CardContent>
              <CardHeader
                avatar={testimonial.avatar}
                title={testimonial.name}
                subheader={testimonial.occupation}
              />
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
